import React, { useState, useEffect } from 'react';
import { useModal } from '../../contexts/ModalContext';
import { PleriModal } from '../common/PleriModal';
import { useRenameConversation } from '../../api/conversations';
import { Button } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

export const RenameConversationModal: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const {
    renameModalOpen,
    conversationToRename,
    conversationTitle,
    closeRenameModal,
  } = useModal();
  const { mutate: renameConversation, isPending: isRenamingState } =
    useRenameConversation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Pre-populate form with existing title when modal opens
  useEffect(() => {
    if (renameModalOpen && conversationTitle) {
      setNewTitle(conversationTitle);
    } else if (renameModalOpen) {
      setNewTitle('');
    }
  }, [renameModalOpen, conversationTitle]);

  const confirmRenameConversation = () => {
    if (!conversationToRename || !newTitle.trim() || isProcessing) {
      return;
    }

    setIsProcessing(true);

    // Call the rename mutation
    renameConversation(
      { conversationId: conversationToRename, title: newTitle.trim() },
      {
        onSuccess: () => {
          setIsProcessing(false);
          closeRenameModal();
        },
        onError: () => {
          setIsProcessing(false);
          closeRenameModal();
        },
      },
    );
  };

  const isLoading = isRenamingState || isProcessing;
  const trimmedTitle = newTitle.trim();
  const isFormValid = trimmedTitle.length >= 1 && trimmedTitle.length <= 200;

  return (
    <PleriModal
      isOpen={renameModalOpen}
      title={t('renameChat')}
      onClose={closeRenameModal}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="chat-title"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('chatTitle')}
          </label>
          <input
            id="chat-title"
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t('enterChatTitle')}
            className="border-lighter-gray bg-action-secondary w-full rounded-md border-[0.3px] px-3 py-2 shadow-sm focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none"
            disabled={isLoading}
            autoFocus
            minLength={1}
            maxLength={200}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isFormValid && !isLoading) {
                confirmRenameConversation();
              }
            }}
          />
          <div className="mt-1 text-right text-xs text-gray-500 dark:text-gray-400">
            {trimmedTitle.length}/200 characters
          </div>
        </div>
        <div className="flex justify-end space-x-3">
          <Button
            variant="secondary"
            label={t('common:cancel')}
            data-testid="cancel-rename-conversation"
            onClick={closeRenameModal}
            isDisabled={isLoading}
            size="small"
          />
          <Button
            label={isLoading ? t('renaming') : t('rename')}
            data-testid="confirm-rename-conversation"
            onClick={confirmRenameConversation}
            isDisabled={isLoading || !isFormValid}
            variant="primary"
            size="small"
          />
        </div>
      </div>
    </PleriModal>
  );
};
